/* eslint-disable */
import { html } from 'lit';
import { query } from 'lit/decorators.js';
import { state } from 'lit/decorators.js';
import type { SynAttributesChangedEvent } from '../../events/syn-attributes-changed.js';
import componentStyles from '../../styles/component.styles.js';
import SynergyElement from '../../internal/synergy-element.js';
import styles from './menu.styles.js';
import type { CSSResultGroup } from 'lit';
import type SynMenuItem from '../menu-item/menu-item.component.js';

export interface MenuSelectEventDetail {
  item: SynMenuItem;
}

/**
 * @summary Menus provide a list of options for the user to choose from.
 * @documentation https://synergy-design-system.github.io/?path=/docs/components-syn-menu--docs
 * @status stable
 * @since 1.12.0
 *
 * @slot - The menu's content, including menu items, menu labels, and dividers.
 *
 * @event {{ item: SynMenuItem }} syn-select - Emitted when a menu item is selected.
 */
export default class SynMenu extends SynergyElement {
  static styles: CSSResultGroup = [componentStyles, styles];

  @query('slot') defaultSlot: HTMLSlotElement;
  @state() hasMenuItemsWithCheckmarks = false;
  private checkmarkStyledItems = new Set<SynMenuItem>();

  private handleUpdateCheckmarks(items: SynMenuItem[]) {
    // #368: Treat a menu as having checkmarks if it has any checkboxes or items with loading states
    // The loading indicator has to be checked as well, as it's specially placed over the check mark
    this.hasMenuItemsWithCheckmarks = items.some(item => item.type === 'checkbox' || item.loading);
    this.syncCheckmarkVisibility(items);
  }

  private syncCheckmarkVisibility(items: SynMenuItem[]) {
    this.checkmarkStyledItems.forEach(item => {
      if (!items.includes(item)) {
        item.style.removeProperty('--display-checkmark');
        this.checkmarkStyledItems.delete(item);
      }
    });

    if (this.hasMenuItemsWithCheckmarks) {
      items.forEach(item => {
        item.style.removeProperty('--display-checkmark');
        this.checkmarkStyledItems.delete(item);
      });
      return;
    }

    items.forEach(item => {
      item.style.setProperty('--display-checkmark', 'none');
      this.checkmarkStyledItems.add(item);
    });
  }

  private updateCheckMarksByChildPropChange = (e: SynAttributesChangedEvent) => {
    e.stopImmediatePropagation();
    this.handleUpdateCheckmarks(this.getAllItems());
  };
  
  disconnectedCallback() {
    this.removeEventListener('syn-attributes-changed', this.updateCheckMarksByChildPropChange);
    this.checkmarkStyledItems.forEach(item => item.style.removeProperty('--display-checkmark'));
    this.checkmarkStyledItems.clear();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'menu');
    this.addEventListener('syn-attributes-changed', this.updateCheckMarksByChildPropChange);
  }

  private getMenuItemFromEvent(event: Event) {
    const composedPath = event.composedPath();
    const target = composedPath.find((el: EventTarget) => el instanceof HTMLElement && this.isMenuItem(el));

    if (!target || !(target instanceof HTMLElement)) {
      return undefined;
    }

    const closestMenu = composedPath.find((el: EventTarget) => el instanceof Element && el.getAttribute('role') === 'menu');
    if (closestMenu !== this) {
      return undefined;
    }

    return target as SynMenuItem;
  }

  private handleClick(event: MouseEvent) {
    const item = this.getMenuItemFromEvent(event);

    if (!item) return;

    if (item.type === 'checkbox') {
      item.checked = !item.checked;
    }

    this.emit('syn-select', { detail: { item } });
  }

  private handleKeyDown(event: KeyboardEvent) {
    // Make a selection when pressing enter or space
    if (event.key === 'Enter' || event.key === ' ') {
      const item = this.getCurrentItem();
      event.preventDefault();
      event.stopPropagation();

      // Simulate a click to support @click handlers on menu items that also work with the keyboard
      item?.click();
    }

    // Move the selection when pressing down or up
    else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      const items = this.getAllItems();
      const activeItem = this.getCurrentItem();
      let index = activeItem ? items.indexOf(activeItem) : 0;

      if (items.length > 0) {
        event.preventDefault();
        event.stopPropagation();

        if (event.key === 'ArrowDown') {
          index++;
        } else if (event.key === 'ArrowUp') {
          index--;
        } else if (event.key === 'Home') {
          index = 0;
        } else if (event.key === 'End') {
          index = items.length - 1;
        }

        if (index < 0) {
          index = items.length - 1;
        }
        if (index > items.length - 1) {
          index = 0;
        }

        this.setCurrentItem(items[index]);
        items[index].focus();
      }
    }
  }

  private handleMouseDown(event: MouseEvent) {
    const target = this.getMenuItemFromEvent(event);

    if (target) {
      this.setCurrentItem(target);
    }
  }

  private handleSlotChange() {
    const items = this.getAllItems();
    this.handleUpdateCheckmarks(items);

    // Reset the roving tab index when the slotted items change
    if (items.length > 0) {
      this.setCurrentItem(items[0]);
    }
  }

  private isMenuItem(item: HTMLElement) {
    return (
      item.tagName.toLowerCase() === 'syn-menu-item' ||
      ['menuitem', 'menuitemcheckbox', 'menuitemradio'].includes(item.getAttribute('role') ?? '')
    );
  }

  private getMenuItemsFromElement(element: HTMLElement): SynMenuItem[] {
    if (element.inert) {
      return [];
    }

    if (this.isMenuItem(element)) {
      return [element as SynMenuItem];
    }

    if (element.tagName.toLowerCase() === 'syn-menu') {
      return [];
    }

    return [...element.children].flatMap(child => this.getMenuItemsFromElement(child as HTMLElement));
  }

  /** @internal Gets all slotted menu items, ignoring dividers, headers, and other elements. */
  getAllItems() {
    return [...this.defaultSlot.assignedElements({ flatten: true })].flatMap(el => this.getMenuItemsFromElement(el as HTMLElement));
  }

  /**
   * @internal Gets the current menu item, which is the menu item that has `tabindex="0"` within the roving tab index.
   * The menu item may or may not have focus, but for keyboard interaction purposes it's considered the "active" item.
   */
  getCurrentItem() {
    return this.getAllItems().find(i => i.getAttribute('tabindex') === '0');
  }

  /**
   * @internal Sets the current menu item to the specified element. This sets `tabindex="0"` on the target element and
   * `tabindex="-1"` to all other items. This method must be called prior to setting focus on a menu item.
   */
  setCurrentItem(item: SynMenuItem) {
    const items = this.getAllItems();

    // Update tab indexes
    items.forEach(i => {
      i.setAttribute('tabindex', i === item ? '0' : '-1');
    });
  }

  render() {
    return html`
      <slot
        @slotchange=${this.handleSlotChange}
        @click=${this.handleClick}
        @keydown=${this.handleKeyDown}
        @mousedown=${this.handleMouseDown}
      ></slot>
    `;
  }
}
